import { useMemo } from 'react'
import type { Transaction } from '../../api/types'
import { useT, type Translate } from '../../i18n'
import type { BusinessTime } from '../../time/businessTime'
import { DataTable, ServiceTypeTag, StatusBadge, type Column, type SortDirection } from '../../ui'
import {
  FIELD_BENEFICIARY, FIELD_BILL_NUMBER, FIELD_COLLECTED_TOTAL, FIELD_COLLECTOR_COMPANY_BID, FIELD_CUS_NMB, FIELD_FEE, FIELD_GATEWAY, FIELD_PAID_AT,
  FIELD_SERVICE, FIELD_SERVICE_TYPE, FIELD_SOURCE_STATUS, FIELD_STATUS_UPDATED_AT, FIELD_SVC_NMB,
  type ExposedFields,
} from './exposure'
import { statusLabel, statusTone } from './statusTone'
import screen from '../screen.module.css'

export interface TransactionsTableProps {
  readonly rows: readonly Transaction[]
  readonly businessTime: BusinessTime
  readonly sortBy: string
  readonly sortDir: SortDirection
  readonly onSort: (sortKey: string) => void
  readonly onSelect: (row: Transaction) => void
  readonly selectedId: string | undefined
  readonly stale: boolean
  readonly caption: string
  /** FR-38: the grantable fields the page's policy exposes. The gateway,
   *  service and service type columns exist exactly when listed here. */
  readonly exposed: ExposedFields
  /** Resolves a row's gateway to the biller holding it, for the biller column
   *  an internal user sees in all-billers mode. Undefined omits the column —
   *  for a biller user every row would repeat their own name. Returning ''
   *  means the gateway is not assigned to any biller yet. */
  readonly billerOfGateway?: (gatewayId: string) => string
}

function buildColumns(
  t: Translate,
  businessTime: BusinessTime,
  exposed: ExposedFields,
  billerOfGateway?: (gatewayId: string) => string,
  hasSettlementDay = false,
): ReadonlyArray<Column<Transaction>> {
  return [
    {
      key: 'date',
      header: t('tx.date'),
      sortKey: 'date',
      render: (row) => businessTime.dateTime(row.created_at),
    },
    // FR-41: the API derives the settlement day for a beneficiary with a
    // cut-off and stamps every row or none; the column follows the rows.
    ...(hasSettlementDay
      ? [{
          key: 'settlement_day',
          header: t('tx.settlementDayColumn'),
          render: (row: Transaction) => (row.settlement_day ? businessTime.settlementDay(row.settlement_day) : ''),
        }]
      : []),
    {
      key: 'ptn',
      // The filter bar searches by PTN, so the list must show it — otherwise a
      // biller can filter on a value they cannot see, and cannot copy one out
      // of a row to search for it.
      header: t('tx.ptn'),
      render: (row) => <span className={screen.mono}>{row.ptn}</span>,
    },
    {
      // FR-39: the name Ops gave the service for this biller — default tier,
      // every requester, "n/a" while none is set. Smobilpay's own service
      // name is the grantable column below.
      key: 'service_label',
      header: t('tx.serviceLabel'),
      render: (row) => row.service_label || t('tx.notAvailable'),
    },
    // FR-38: gateway, service and service type are grantable fields. Each
    // column exists exactly when the PAGE's policy exposes the field — never
    // by looking at which keys the rows carry. A row without a value under an
    // exposed field (a grant restricted to some service types) shows a dash.
    ...(exposed.has(FIELD_GATEWAY)
      ? [
          {
            key: 'gateway',
            header: t('tx.gateway'),
            // gateway_name, not gateway_id: the two identifier formats diverge
            // between biller-tx-sync-service ("12") and the portal registry ("GW-MMC-001"), so
            // the id is treated as an opaque key and never shown as a label.
            render: (row: Transaction) => row.gateway_name ?? '—',
          },
        ]
      : []),
    ...(billerOfGateway
      ? [
          {
            key: 'biller',
            header: t('tx.biller'),
            // Resolved through the gateway: the warehouse row does not carry a
            // biller, the gateway assignment does — the same rule the server
            // scopes by. A gateway nobody holds is a real state (the mapping
            // screen calls it unmapped), so it gets a word, not a blank.
            render: (row: Transaction) => billerOfGateway(row.gateway_id) || t('tx.biller.unassigned'),
          },
        ]
      : []),
    ...(exposed.has(FIELD_SERVICE)
      ? [{ key: 'service', header: t('tx.service'), render: (row: Transaction) => row.service_name ?? '—' }]
      : []),
    ...(exposed.has(FIELD_SERVICE_TYPE)
      ? [
          {
            key: 'service_type',
            header: t('tx.serviceType'),
            // The same tag (and colour) the field exposure screen shows for this
            // type — one visual vocabulary for service types everywhere.
            render: (row: Transaction) => (row.service_type ? <ServiceTypeTag type={row.service_type} /> : '—'),
          },
        ]
      : []),
    // The identity is listed only for an internal user acting for a
    // beneficiary organisation (FR-42, AB-40), so the column follows exposure.
    ...(exposed.has(FIELD_BENEFICIARY)
      ? [
          {
            key: 'beneficiary',
            header: t('tx.beneficiary'),
            render: (row: Transaction) => <span className={screen.mono}>{row.beneficiary_code ?? ''}</span>,
          },
        ]
      : []),
    {
      // PRD FR-16 requires this in the LIST, not only the detail panel. BRD A5
      // calls it "Service number e.g. Mobile money / airtime = phone number,
      // bill payment = service number" — it is how a reconciliation analyst
      // finds one customer's transaction, so burying it a click away defeats
      // the screen's primary job.
      //
      // Named "payer_reference" on the wire but it is `p.svc_nmb` in Core: the
      // SERVICE number, not an agent's or a payer's reference. The label says
      // what it is.
      key: 'payer_reference',
      header: t('tx.payerReference'),
      render: (row) => <span className={screen.mono}>{row.payer_reference || '—'}</span>,
    },
    // MPAY-33568: the provider's own id, default tier since AB-39. Empty until
    // the provider has called back.
    {
      key: 'provider_txid',
      header: t('tx.providerTxid'),
      render: (row) => <span className={screen.mono}>{row.provider_txid}</span>,
    },
    // The rest of the grantable tier, in catalogue order (MPAY-33570). The
    // export always carried a granted field as a column; the list did not, so
    // granting one of these changed the download and nothing on screen.
    // Remittance details is the exception, decided 2026-10-08: the raw
    // remittance document shows in the detail panel and the export, never here.
    // Customer data (CDATA) is the other, for the same reason (AB-41).
    ...(exposed.has(FIELD_FEE)
      ? [{ key: 'fee', header: t('tx.fee'), numeric: true, render: (row: Transaction) => (row.fee === undefined ? '—' : businessTime.money(row.fee)) }]
      : []),
    ...(exposed.has(FIELD_COLLECTED_TOTAL)
      ? [{
          key: 'collected_total',
          header: t('tx.collectedTotal'),
          numeric: true,
          render: (row: Transaction) => (row.collected_total === undefined ? '—' : businessTime.money(row.collected_total)),
        }]
      : []),
    ...(exposed.has(FIELD_PAID_AT)
      ? [{ key: 'paid_at', header: t('tx.paidAt'), render: (row: Transaction) => (row.paid_at ? businessTime.dateTime(row.paid_at) : '—') }]
      : []),
    ...(exposed.has(FIELD_STATUS_UPDATED_AT)
      ? [{
          key: 'status_updated_at',
          header: t('tx.statusUpdatedAt'),
          render: (row: Transaction) => (row.status_updated_at ? businessTime.dateTime(row.status_updated_at) : '—'),
        }]
      : []),
    ...(exposed.has(FIELD_SOURCE_STATUS)
      ? [{ key: 'source_status', header: t('tx.sourceStatus'), render: (row: Transaction) => <span className={screen.mono}>{row.source_status || '—'}</span> }]
      : []),
    // The receipt fields (FR-43): grantable, so each column exists exactly
    // when the policy exposes it.
    ...(exposed.has(FIELD_BILL_NUMBER)
      ? [{ key: 'bill_number', header: t('tx.billNumber'), render: (row: Transaction) => <span className={screen.mono}>{row.bill_number ?? ''}</span> }]
      : []),
    ...(exposed.has(FIELD_SVC_NMB)
      ? [{ key: 'svc_nmb', header: t('tx.svcNmb'), render: (row: Transaction) => <span className={screen.mono}>{row.svc_nmb ?? ''}</span> }]
      : []),
    ...(exposed.has(FIELD_CUS_NMB)
      ? [{ key: 'cus_nmb', header: t('tx.cusNmb'), render: (row: Transaction) => <span className={screen.mono}>{row.cus_nmb ?? ''}</span> }]
      : []),
    // MPAY-33569: the collecting partner's BID (sensitive), last in the catalogue.
    ...(exposed.has(FIELD_COLLECTOR_COMPANY_BID)
      ? [{ key: 'collector_company_bid', header: t('tx.collectorCompanyBid'), render: (row: Transaction) => <span className={screen.mono}>{row.collector_company_bid ?? ''}</span> }]
      : []),
    {
      key: 'amount',
      header: t('tx.amount'),
      sortKey: 'amount',
      numeric: true,
      render: (row) => businessTime.money(row.amount),
    },
    {
      key: 'status',
      header: t('tx.status'),
      render: (row) => <StatusBadge tone={statusTone(row.transaction_status)}>{statusLabel(t, row.transaction_status)}</StatusBadge>,
    },
  ]
}

export function TransactionsTable({
  rows, businessTime, sortBy, sortDir, onSort, onSelect, selectedId, stale, caption, exposed, billerOfGateway,
}: TransactionsTableProps) {
  const t = useT()
  const columns = useMemo(
    () => buildColumns(t, businessTime, exposed, billerOfGateway, rows.some((r) => Boolean(r.settlement_day))),
    [t, businessTime, exposed, billerOfGateway, rows],
  )

  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={rows}
      rowKey={(row) => row.ptn}
      sort={{ by: sortBy, dir: sortDir }}
      onSort={onSort}
      onRowClick={onSelect}
      selectedKey={selectedId}
      stale={stale}
      sortLabel={(column, next) =>
        t(next === 'asc' ? 'tx.sortAsc' : 'tx.sortDesc', { column })
      }
    />
  )
}
